import React, { useContext, useEffect, useState } from "react";
import { StyleSheet, View, Dimensions } from "react-native";
import { Modal, Text, TouchableOpacity } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useSelector } from "react-redux";
import { useTranslation } from "../../../utils/useTranslation";
import Icon from "react-native-vector-icons/FontAwesome5";
import { Button } from "react-native-paper";
import { exportDataToExcel } from "../../../utils/exportDataToExcel";

interface RequestDateAlertsModalProps {
  visible: boolean;
  onClose: any;
}

const ChinaTrackerRequestDateAlertsModal: React.FC<
  RequestDateAlertsModalProps
> = ({ visible, onClose }) => {
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get("window").width,
  );
  const { approvedOrders: orders, loading } = useSelector(
    (state: any) => state.chinaTracker,
  );
  const approvedOrders = orders.filter(
    (order: any) =>
      order.OrderStatus !== "Closed" && order.POStatus !== "Closed",
  );
  const [filteredOrders, setFilteredOrders] = useState<any[]>([]);
  const { t } = useTranslation();
  const { isLargeDevice, isMediumDevice, isSmallDevice, isMobileDevice } =
    useContext(DeviceOrientationContext);

  // Date filter state: "all", "7days", "14days", "30days"
  const [dateFilter, setDateFilter] = useState<string>("7days");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const isMobile = isSmallDevice || isMobileDevice;

  const getFilterLabel = (filter: string) => {
    switch (filter) {
      case "all":
        return "All";
      case "overdue":
        return t("overdue");
      case "7days":
        return t("next7Days");
      case "14days":
        return t("next14Days");
      case "30days":
        return t("next30Days");
      default:
        return filter;
    }
  };

  const parseDate = (dateString: string) => {
    if (!dateString) return null;
    const trimmed = dateString.trim();
    const [day, month, year] = trimmed.split("/");
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  const calculateDaysUntilDue = (requestDateString: string) => {
    const requestDate = parseDate(requestDateString);
    if (!requestDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    requestDate.setHours(0, 0, 0, 0);

    const diffTime = requestDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  const getUrgencyColor = (days: number) => {
    if (days < 0) return "#e74c3c"; // overdue - red
    if (days <= 7) return "#e67e22"; // urgent - orange
    if (days <= 14) return "#f39c12"; // warning - yellow/orange
    if (days <= 30) return "#3498db"; // upcoming - blue
    return "#95a5a6"; // future - gray
  };

  const getUrgencyLabelKey = (days: number) => {
    if (days < 0) return "urgencyOverdue";
    if (days === 0) return "urgencyDueToday";
    if (days === 1) return "urgencyDueTomorrow";
    if (days <= 7) return "urgencyUrgent";
    if (days <= 14) return "urgencySoon";
    if (days <= 30) return "urgencyUpcoming";
    return "urgencyFuture";
  };

  useEffect(() => {
    if (!approvedOrders || approvedOrders.length === 0) {
      setFilteredOrders([]);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate days until due for all orders
    const ordersWithDays = approvedOrders.map((order: any) => {
      const daysUntilDue = calculateDaysUntilDue(order.RequestDate);
      return {
        ...order,
        DaysUntilDue: daysUntilDue,
        urgencyColor:
          daysUntilDue !== null ? getUrgencyColor(daysUntilDue) : "#95a5a6",
        urgencyLabelKey:
          daysUntilDue !== null
            ? getUrgencyLabelKey(daysUntilDue)
            : "notAvailable",
      };
    });

    // Filter based on selected date range
    let filtered = ordersWithDays.filter((order: any) => {
      if (order.DaysUntilDue === null) return false;

      switch (dateFilter) {
        case "7days":
          return order.DaysUntilDue >= 0 && order.DaysUntilDue <= 7;
        case "14days":
          return order.DaysUntilDue >= 0 && order.DaysUntilDue <= 14;
        case "30days":
          return order.DaysUntilDue >= 0 && order.DaysUntilDue <= 30;
        case "overdue":
          return order.DaysUntilDue < 0;
        case "all":
        default:
          return true;
      }
    });

    // Sort by days until due (ascending - most urgent first)
    filtered.sort((a: any, b: any) => {
      if (a.DaysUntilDue === null) return 1;
      if (b.DaysUntilDue === null) return -1;
      return a.DaysUntilDue - b.DaysUntilDue;
    });

    setFilteredOrders(filtered);
  }, [approvedOrders, dateFilter]);

  useEffect(() => {
    const updateDimensions = () => {
      const { width } = Dimensions.get("window");
      setScreenWidth(width);
    };

    const dimensionsListener = Dimensions.addEventListener(
      "change",
      updateDimensions,
    );

    return () => {
      dimensionsListener.remove();
    };
  }, []);

  const getModalWidth = () => {
    if (isSmallDevice) return "95%";
    if (isMediumDevice) return "85%";
    return "80%";
  };

  const getDueDisplayText = (daysUntilDue: number | null) => {
    if (typeof daysUntilDue !== "number") {
      return t("notAvailable");
    }

    if (daysUntilDue < 0) {
      const absDays = Math.abs(daysUntilDue);
      const suffix = absDays === 1 ? t("dayAgo") : t("daysAgo");
      return `${absDays} ${suffix}`;
    }

    if (daysUntilDue === 0) {
      return t("today");
    }

    const label = daysUntilDue === 1 ? t("day") : t("days");
    return `${daysUntilDue} ${label}`;
  };

  const getOrderStatusLabel = (status?: string) => {
    if (!status) {
      return t("noData");
    }

    switch (status) {
      case "At Risk":
        return t("atRisk");
      case "On Time":
        return t("onTime");
      case "Delayed":
        return t("delayed");
      case "Planned":
        return t("planned");
      case "No Data":
        return t("noData");
      case "Closed":
        return t("closed");
      case "Completed":
        return t("completed");
      case "Interrupted":
        return t("interrupted");
      case "Cancelled":
        return t("cancelled");
      default:
        return status;
    }
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={[styles.modalContainer, { width: getModalWidth() }]}>
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Icon name="bell" size={24} color="#e74c3c" />
              <Text style={styles.title}>{t("requestDateAlerts")}</Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeIconButton}>
              <Icon name="times" size={20} color="#666" />
            </TouchableOpacity>
          </View>

          <View style={styles.summaryContainer}>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryNumber}>{filteredOrders.length}</Text>
              <Text style={styles.summaryLabel}>{t("ordersLabel")}</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={[styles.summaryNumber, { color: "#e74c3c" }]}>
                {filteredOrders.filter((o) => o.DaysUntilDue < 0).length}
              </Text>
              <Text style={styles.summaryLabel}>{t("overdue")}</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={[styles.summaryNumber, { color: "#e67e22" }]}>
                {
                  filteredOrders.filter(
                    (o) => o.DaysUntilDue >= 0 && o.DaysUntilDue <= 7,
                  ).length
                }
              </Text>
              <Text style={styles.summaryLabel}>{t("next7Days")}</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={[styles.summaryNumber, { color: "#3498db" }]}>
                {
                  filteredOrders.filter(
                    (o) => o.DaysUntilDue > 7 && o.DaysUntilDue <= 30,
                  ).length
                }
              </Text>
              <Text style={styles.summaryLabel}>{t("next30Days")}</Text>
            </View>
          </View>

          <View style={styles.filtersContainer}>
            {isMobile ? (
              <View>
                <View style={styles.mobileFilterRow}>
                  <TouchableOpacity
                    style={[
                      styles.mobileDropdownButton,
                      mobileFilterOpen && styles.mobileDropdownButtonActive,
                    ]}
                    onPress={() => setMobileFilterOpen(!mobileFilterOpen)}
                  >
                    <Text style={styles.mobileDropdownLabel}>Filter: </Text>
                    <Text style={styles.mobileDropdownValue}>
                      {getFilterLabel(dateFilter)}
                    </Text>
                    <Text style={styles.mobileDropdownArrow}>
                      {mobileFilterOpen ? "▲" : "▼"}
                    </Text>
                  </TouchableOpacity>
                  {!mobileFilterOpen && (
                    <TouchableOpacity
                      onPress={() =>
                        exportDataToExcel(
                          filteredOrders.flatMap(
                            ({
                              OrderNumber1,
                              MSRef,
                              ItemName,
                              ItemCode,
                              TotalOrdered,
                              RequestDate,
                              StateDelivery,
                              OrderStatus,
                              FulfillmentPercent,
                              QtyToShip,
                              runOrders, // array of { runOrder, planStartDay }
                            }) => {
                              const runs =
                                Array.isArray(runOrders) && runOrders.length > 0
                                  ? runOrders
                                  : [{ runOrder: null, planStartDay: null }]; // fallback: one row with blanks

                              return runs.map(({ runOrder, planStartDay }) => ({
                                "Order No": OrderNumber1,
                                MSRef,
                                Item: ItemName,
                                Code: ItemCode,
                                Quantity: TotalOrdered,
                                "Due Date": RequestDate,
                                State: StateDelivery,
                                Status: OrderStatus,
                                "Fulfillment %":
                                  Number(FulfillmentPercent).toFixed(2),
                                QtyToShip: QtyToShip.toLocaleString(),
                                "Run Order": runOrder ?? "",
                                "Plan Start Date": planStartDay
                                  ? new Date(planStartDay).toLocaleDateString(
                                      "en-AU",
                                    )
                                  : "",
                              }));
                            },
                          ) || [],
                          "ChinaAlertsData",
                          "China Alerts Data",
                        )
                      }
                      style={[styles.filterButton, styles.filterButtonActive]}
                    >
                      <Text
                        style={[
                          styles.filterButtonText,
                          styles.filterButtonTextActive,
                        ]}
                      >
                        <Icon name="file-excel" size={16} color="#fff" /> Export
                        as CSV
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
                {mobileFilterOpen && (
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={styles.mobileChipRow}
                  >
                    {(
                      ["all", "overdue", "7days", "14days", "30days"] as const
                    ).map((filter) => (
                      <TouchableOpacity
                        key={filter}
                        style={[
                          styles.filterChip,
                          dateFilter === filter && styles.filterChipActive,
                        ]}
                        onPress={() => {
                          setDateFilter(filter);
                          if (filter !== dateFilter) {
                            setMobileFilterOpen(false);
                          }
                        }}
                      >
                        <Text
                          style={
                            dateFilter === filter
                              ? styles.filterChipTextActive
                              : styles.filterChipText
                          }
                        >
                          {getFilterLabel(filter)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                )}
              </View>
            ) : (
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                {/* Left Scroll Filters */}
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <TouchableOpacity
                    onPress={() => setDateFilter("all")}
                    style={[
                      styles.filterButton,
                      dateFilter === "all" && styles.filterButtonActive,
                    ]}
                  >
                    <Text
                      style={[
                        styles.filterButtonText,
                        dateFilter === "all" && styles.filterButtonTextActive,
                      ]}
                    >
                      All
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => setDateFilter("overdue")}
                    style={[
                      styles.filterButton,
                      dateFilter === "overdue" && styles.filterButtonActive,
                    ]}
                  >
                    <Text
                      style={[
                        styles.filterButtonText,
                        dateFilter === "overdue" &&
                          styles.filterButtonTextActive,
                      ]}
                    >
                      {t("overdue")}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => setDateFilter("7days")}
                    style={[
                      styles.filterButton,
                      dateFilter === "7days" && styles.filterButtonActive,
                    ]}
                  >
                    <Text
                      style={[
                        styles.filterButtonText,
                        dateFilter === "7days" && styles.filterButtonTextActive,
                      ]}
                    >
                      {t("next7Days")}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => setDateFilter("14days")}
                    style={[
                      styles.filterButton,
                      dateFilter === "14days" && styles.filterButtonActive,
                    ]}
                  >
                    <Text
                      style={[
                        styles.filterButtonText,
                        dateFilter === "14days" &&
                          styles.filterButtonTextActive,
                      ]}
                    >
                      {t("next14Days")}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => setDateFilter("30days")}
                    style={[
                      styles.filterButton,
                      dateFilter === "30days" && styles.filterButtonActive,
                    ]}
                  >
                    <Text
                      style={[
                        styles.filterButtonText,
                        dateFilter === "30days" &&
                          styles.filterButtonTextActive,
                      ]}
                    >
                      {t("next30Days")}
                    </Text>
                  </TouchableOpacity>
                </ScrollView>

                {/* Export To CSV */}
                <TouchableOpacity
                  onPress={() =>
                    exportDataToExcel(
                      filteredOrders.flatMap(
                        ({
                          OrderNumber1,
                          MSRef,
                          ItemName,
                          ItemCode,
                          TotalOrdered,
                          RequestDate,
                          StateDelivery,
                          OrderStatus,
                          FulfillmentPercent,
                          QtyToShip,
                          runOrders, // array of { runOrder, planStartDay }
                        }) => {
                          const runs =
                            Array.isArray(runOrders) && runOrders.length > 0
                              ? runOrders
                              : [{ runOrder: null, planStartDay: null }]; // fallback: one row with blanks

                          return runs.map(({ runOrder, planStartDay }) => ({
                            "Order No": OrderNumber1,
                            MSRef,
                            Item: ItemName,
                            Code: ItemCode,
                            Quantity: TotalOrdered,
                            "Due Date": RequestDate,
                            State: StateDelivery,
                            Status: OrderStatus,
                            "Fulfillment %":
                              Number(FulfillmentPercent).toFixed(2),
                            QtyToShip: QtyToShip.toLocaleString(),
                            "Run Order": runOrder ?? "",
                            "Plan Start Date": planStartDay
                              ? new Date(planStartDay).toLocaleDateString(
                                  "en-AU",
                                )
                              : "",
                          }));
                        },
                      ) || [],
                      "ChinaAlertsData",
                      "China Alerts Data",
                    )
                  }
                  color="rgb(47, 149, 220)"
                  contentStyle={{
                    paddingTop: "10%",
                    paddingBottom: "10%",
                    paddingLeft: "25%",
                  }}
                  style={[styles.filterButton, styles.filterButtonActive]}
                >
                  <Text
                    style={[
                      styles.filterButtonText,
                      styles.filterButtonTextActive,
                    ]}
                  >
                    <Icon name="file-excel" size={16} color="#fff" /> Export as
                    CSV
                  </Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          <ScrollView style={styles.contentContainer}>
            {loading ? (
              <View style={styles.loadingContainer}>
                <Spinner size="small" color="#56B7FD" />
                <Text style={styles.loadingText}>{t("loadingOrders")}</Text>
              </View>
            ) : filteredOrders.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Icon name="check-circle" size={48} color="#2ecc71" />
                <Text style={styles.emptyText}>{t("noOrdersInTimeframe")}</Text>
                <Text style={styles.emptySubtext}>{t("allCaughtUp")}</Text>
              </View>
            ) : (
              <View style={styles.tableContainer}>
                {filteredOrders.map((order, index) => (
                  <View key={order.Key || index} style={styles.orderCard}>
                    <View style={styles.orderHeader}>
                      <View style={styles.orderHeaderLeft}>
                        <Text style={styles.orderNumber}>
                          {order.MSRef || order.OrderNumber1}
                        </Text>
                        <View
                          style={[
                            styles.urgencyBadge,
                            { backgroundColor: order.urgencyColor },
                          ]}
                        >
                          <Text style={styles.urgencyBadgeText}>
                            {t(order.urgencyLabelKey || "notAvailable")}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.daysContainer}>
                        <Text
                          style={[
                            styles.daysNumber,
                            { color: order.urgencyColor },
                          ]}
                        >
                          {getDueDisplayText(order.DaysUntilDue)}
                        </Text>
                      </View>
                    </View>

                    <View style={styles.orderDetails}>
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Order No:</Text>
                        <Text style={styles.detailValue}>
                          {order.OrderNumber1}
                        </Text>
                      </View>
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Item:</Text>
                        <Text style={styles.detailValue}>{order.ItemName}</Text>
                      </View>
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>
                          {`${t("codeLabel")}:`}
                        </Text>
                        <Text style={styles.detailValue}>{order.ItemCode}</Text>
                      </View>
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>
                          {`${t("quantityLabel")}:`}
                        </Text>
                        <Text style={styles.detailValue}>
                          {order.TotalOrdered}
                        </Text>
                      </View>
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>
                          {`${t("dueDate")}:`}
                        </Text>
                        <Text
                          style={[styles.detailValue, { fontWeight: "600" }]}
                        >
                          {order.RequestDate}
                        </Text>
                      </View>
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>
                          {`${t("state")}:`}
                        </Text>
                        <Text style={styles.detailValue}>
                          {order.StateDelivery}
                        </Text>
                      </View>
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>
                          {`${t("statusLabel")}:`}
                        </Text>
                        <Text
                          style={[
                            styles.detailValue,
                            {
                              color: "#2ecc71",
                              fontWeight: "600",
                            },
                          ]}
                        >
                          {getOrderStatusLabel(order.OrderStatus)}
                        </Text>
                      </View>

                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>
                          {`${t("fulfillmentPercent")}:`}
                        </Text>
                        <Text
                          style={[
                            styles.detailValue,
                            {
                              color: "red",
                              fontWeight: "600",
                            },
                          ]}
                        >
                          {Number(order.FulfillmentPercent).toFixed(2)} %
                        </Text>
                      </View>

                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>
                          {`${t("qtyToShip")}:`}
                        </Text>
                        <Text
                          style={[
                            styles.detailValue,
                            {
                              fontWeight: "600",
                            },
                          ]}
                        >
                          {order.QtyToShip.toLocaleString()}
                        </Text>
                      </View>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </ScrollView>

          <View style={styles.footer}>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>{t("close")}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  modalContainer: {
    backgroundColor: "#fff",
    borderRadius: 12,
    maxHeight: "90%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  title: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#333",
  },
  closeIconButton: {
    padding: 8,
  },
  summaryContainer: {
    flexDirection: "row",
    justifyContent: "space-around",
    padding: 20,
    backgroundColor: "#f8f9fa",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  summaryItem: {
    alignItems: "center",
  },
  summaryNumber: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#333",
  },
  summaryLabel: {
    fontSize: 12,
    color: "#666",
    marginTop: 4,
  },
  filtersContainer: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#ddd",
    marginRight: 10,
    backgroundColor: "#fff",
  },
  filterButtonActive: {
    backgroundColor: "#56B7FD",
    borderColor: "#56B7FD",
  },
  filterButtonText: {
    fontSize: 13,
    color: "#666",
    fontWeight: "500",
  },
  filterButtonTextActive: {
    color: "#fff",
    fontWeight: "600",
  },
  contentContainer: {
    flex: 1,
  },
  loadingContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  loadingText: {
    marginLeft: 12,
    color: "#56B7FD",
    fontSize: 14,
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#333",
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: "#666",
    marginTop: 8,
    textAlign: "center",
  },
  tableContainer: {
    padding: 15,
  },
  orderCard: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  orderHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  orderHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  orderNumber: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#333",
  },
  urgencyBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  urgencyBadgeText: {
    fontSize: 10,
    fontWeight: "700",
    color: "#fff",
  },
  daysContainer: {
    alignItems: "flex-end",
  },
  daysNumber: {
    fontSize: 16,
    fontWeight: "bold",
  },
  orderDetails: {
    gap: 8,
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  detailLabel: {
    fontSize: 13,
    color: "#666",
    fontWeight: "500",
    flex: 0.4,
  },
  detailValue: {
    fontSize: 13,
    color: "#333",
    flex: 0.6,
    textAlign: "right",
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
    alignItems: "center",
  },
  closeButton: {
    backgroundColor: "#56B7FD",
    paddingVertical: 12,
    paddingHorizontal: 40,
    borderRadius: 8,
    minWidth: 120,
    alignItems: "center",
  },
  closeButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  // Mobile dropdown filter styles
  mobileFilterRow: {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 8,
  },
  mobileDropdownButton: {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  mobileDropdownButtonActive: {
    borderColor: "#61B8FF",
    backgroundColor: "#EFF6FF",
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600" as const,
    color: "#6B7280",
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700" as const,
    color: "#1E40AF",
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: "#6B7280",
    marginLeft: 4,
  },
  mobileChipRow: {
    flexDirection: "row" as const,
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  filterChip: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    marginRight: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  filterChipActive: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  filterChipText: {
    fontSize: 13,
    color: "#475569",
    fontWeight: "500" as const,
  },
  filterChipTextActive: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600" as const,
  },
});

export default ChinaTrackerRequestDateAlertsModal;
